import DefaultLayout from '../../components/DefaultLayout';
import { useIntl } from 'react-intl';
import {
  StyledCartPage,
  StyledNotification,
  StyledWholesalePaymentBlock,
} from './style';
import {
  CartActionType,
  FarmProduct,
  PAYMENT_IN_PROGRESS_KEY,
  PendingPaymentCart,
  WholesaleDeliveryCondition,
  WholesaleDeliveryMode,
  readPaymentInProgress,
  stashCartForPayment,
  takePendingPaymentCart,
  useCartDispatch,
  useCartState,
} from 'public/context/CartContext';
import { ProductData } from 'types/ProductData';
import React, { useEffect, useRef, useState } from 'react';
import CartStep from 'enums/cart-step';
import { Button, Icon, SmartIdModal, Spinner } from 'ui';
import Cart from 'public/components/DefaultLayout/Cart';
import ProcessPurchase from 'public/components/DefaultLayout/ProcessPurchase';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { StyledAntdCartSteps } from 'ui/Steps/style';
import {
  Alert,
  Divider,
  Form,
  message,
  notification,
  StepProps,
  Tooltip,
} from 'antd';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { createCartFingerprint } from '../../../utils';
import { useForm } from 'antd/es/form/Form';
import useMarketType from '../../../utils/useMarketType';
import { useCoupons } from '../../components/DefaultLayout/CartSummary/useCoupons';
import PublicRoute from 'enums/publicRoutes';
import WholesaleDelivery from 'public/components/DefaultLayout/WholesaleDelivery';

type BackButton = {
  text: string;
  onClick: () => void;
};

const PENDING_POLL_INTERVAL_MS = 5000;
const PENDING_POLL_MAX_ATTEMPTS = 24;
const PENDING_SLOW_POLL_INTERVAL_MS = 30000;
const PAID_STATUSES = [4, 5]; // OrderPaymentStatusType SUCCESS, PRE_PAID

type ReceivedOrder = { farmProducts: FarmProduct[]; priceTotal: number };

export const CartPage = () => {
  const [isOpenSmartId, setIsOpenSmartId] = useState(false);

  const intl = useIntl();
  const [form] = useForm();
  const [userForm] = Form.useForm();
  const {
    farmProducts,
    priceTotal,
    wholesaleDeliveryMode,
    wholesaleDeliveries,
  } = useCartState();
  const [current, setCurrent] = useState<CartStep>(CartStep.CART);
  const [pageTitle, setPageTitle] = useState<string>('');
  const [backButton, setBackButton] = useState<BackButton>({
    text: intl.formatMessage({ id: 'general.back_to_catalog' }),
    onClick: () => navigate(PublicRoute.PRODUCTS_ALL),
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const pendingOrderNumber =
    searchParams.get('status') === 'pending' ? searchParams.get('order') : null;
  const [isPendingTimedOut, setIsPendingTimedOut] = useState(false);
  // The received-order view renders the order as the server recorded it (frozen
  // prices), never the live cart — the live cart is empty once the order is paid.
  const [receivedOrder, setReceivedOrder] = useState<ReceivedOrder | null>(
    null
  );
  // 'forbidden' = the session that owned the order is gone (idle logout during
  // payment): the order IS paid, the buyer just has to sign in to see it.
  const [receivedOrderError, setReceivedOrderError] = useState<
    'forbidden' | 'error' | null
  >(null);
  const pendingRestoreRef = useRef<PendingPaymentCart | null>(null);
  const fingerPrint = createCartFingerprint(farmProducts);
  const { marketType, isEmarket } = useMarketType();
  const { coupons } = useCoupons();
  const { requiresAuth, cartId } = useCartState();
  const dispatch = useCartDispatch();

  const [repeatOrderProcessed, setRepeatOrderProcessed] = useState(
    () => !localStorage.getItem('pendingRepeatOrder')
  );
  const [notificationApi, contextHolder] = notification.useNotification();

  const { appendData: cartPay, isLoading: isBuying } = useQueryApiClient({
    request: {
      url: `/api/v2/emarket/pay`,
      method: 'POST',
      headers: { 'X-Cart-Fingerprint': fingerPrint },
    },
    onSuccess: (response) => {
      const withoutUpdate = onCartUpdated(response);

      if (withoutUpdate && response) {
        stashCartForPayment(marketType);
        window.location.href = response;
      }
    },
    onError: (response) => {
      console.log(response);

      if (response.code === 412) {
        message.error(intl.formatMessage({ id: response.error }));
        return;
      }

      message.error(intl.formatMessage({ id: 'message.payment_failed' }));
      navigate(
        (isEmarket ? PublicRoute.CART_PAGE : PublicRoute.WHOLESALE_CART) +
          (isEmarket ? '?PROCESS_ORDER=1' : '')
      );
    },
  });

  const { appendData } = useQueryApiClient({
    request: {
      url: `api/v2/products/cart/refetch`,
      method: 'POST',
    },
    onSuccess: (response) => {
      const withoutUpdate = onCartUpdated(response);

      if (withoutUpdate) {
        setCurrent((old) => old + 1);
      }
    },
  });

  const { appendData: fetchPaymentStatus } = useQueryApiClient({
    request: {
      url: 'api/v2/emarket/payment-status/:orderNumber',
      disableOnMount: true,
    },
    onSuccess: (response) => {
      if (response?.status !== 'paid' && response?.status !== 'failed') {
        return;
      }

      const params = new URLSearchParams(searchParams);
      params.set('status', response.status === 'paid' ? 'success' : 'failed');
      params.delete('order');
      setSearchParams(params, { replace: true });
    },
  });

  const { appendData: fetchReceivedOrder } = useQueryApiClient({
    request: {
      url: 'api/v2/emarket/orders/:orderId',
      disableOnMount: true,
      errorWithHeaders: true,
    },
    onSuccess: (response) => {
      setReceivedOrderError(null);
      setReceivedOrder({
        farmProducts: response?.farmProducts ?? [],
        priceTotal: response?.priceTotal ?? 0,
      });
    },
    onError: (response) => {
      setReceivedOrderError(response?.status === 403 ? 'forbidden' : 'error');
    },
  });

  const { appendData: checkStashedOrder } = useQueryApiClient({
    request: {
      url: 'api/v2/emarket/orders/:orderId',
      disableOnMount: true,
    },
    onSuccess: (response) => {
      const pending = pendingRestoreRef.current;
      pendingRestoreRef.current = null;

      if (PAID_STATUSES.includes(response?.paymentStatus)) {
        notificationApi.info({
          message: intl.formatMessage({ id: 'cart.already_paid_message' }),
          description: intl.formatMessage(
            { id: 'cart.already_paid_description' },
            { number: response?.orderNumber ?? '' }
          ),
          duration: 10,
        });
        return;
      }

      if (pending) {
        dispatch({ type: CartActionType.RESTORE_CART, ...pending });
      }
    },
    onError: () => {
      const pending = pendingRestoreRef.current;
      pendingRestoreRef.current = null;

      if (pending) {
        dispatch({ type: CartActionType.RESTORE_CART, ...pending });
      }
    },
  });

  const restoreUnlessPaid = (pending: PendingPaymentCart) => {
    if (!pending.cartId) {
      dispatch({ type: CartActionType.RESTORE_CART, ...pending });
      return;
    }

    pendingRestoreRef.current = pending;
    checkStashedOrder({}, { orderId: pending.cartId });
  };

  useEffect(() => {
    if (!pendingOrderNumber) {
      setIsPendingTimedOut(false);
      return;
    }

    let attempts = 0;
    let poll = window.setInterval(() => {
      attempts += 1;

      if (attempts === PENDING_POLL_MAX_ATTEMPTS) {
        window.clearInterval(poll);
        setIsPendingTimedOut(true);
        poll = window.setInterval(() => {
          fetchPaymentStatus({}, { orderNumber: pendingOrderNumber });
        }, PENDING_SLOW_POLL_INTERVAL_MS);
      }

      fetchPaymentStatus({}, { orderNumber: pendingOrderNumber });
    }, PENDING_POLL_INTERVAL_MS);

    fetchPaymentStatus({}, { orderNumber: pendingOrderNumber });

    return () => window.clearInterval(poll);
  }, [pendingOrderNumber]);

  const onCartUpdated = (response: any) => {
    if (response?.cartUpdated) {
      notificationApi['warning']({
        message: intl.formatMessage({ id: 'cart.updated_message' }),
        description: intl.formatMessage({ id: 'cart.updated_description' }),
        showProgress: true,
        pauseOnHover: true,
        duration: 10,
      });
      setCurrent(0);
    }

    if (response?.marketType) {
      dispatch({
        type: CartActionType.REFETCH_CART_PRODUCTS,
        farmProducts: response.farmProducts,
        priceTotal: response.priceTotal,
        requiresAuth: response.requires_auth,
        cartId: response.cartId,
        cartName: response.cartName,
        marketType: response.marketType,
      });
    }

    return !response?.cartUpdated;
  };

  const handleBuyProducts = async (paymentMethod = 'PAY') => {
    if (!setCurrent) return;

    const shouldSubmit =
      (current !== undefined && current >= CartStep.PROCESS_ORDER) ||
      (!isEmarket && current === CartStep.CART);

    if (shouldSubmit) {
      try {
        await form.validateFields();
        const checkboxes = form?.getFieldsValue();

        if (requiresAuth) {
          setIsOpenSmartId(true);
          return;
        }

        cartPay({
          type: marketType,
          paymentMethod: paymentMethod,
          farmProducts: farmProducts,
          priceTotal: priceTotal,
          personalInfo: isEmarket ? userForm.getFieldsValue() : {},
          ...checkboxes,
          coupons: coupons,
          cartId: cartId,
          wholesaleDeliveryMode: wholesaleDeliveryMode,
          wholesaleDeliveries: wholesaleDeliveries,
        });

        return;
      } catch (e) {
        return;
      }
    } else {
      appendData({
        farmProducts,
        priceTotal,
        coupons,
        marketType,
        cartId,
      });
    }
  };

  useEffect(() => {
    switch (current) {
      case CartStep.CART:
        setPageTitle(intl.formatMessage({ id: 'cart.your_cart_title' }));
        setBackButton({
          text: intl.formatMessage({ id: 'general.back_to_catalog' }),
          onClick: () =>
            navigate(
              isEmarket
                ? PublicRoute.PRODUCTS_ALL
                : PublicRoute.WHOLESALE_PRODUCTS_ALL
            ),
        });
        break;
      case CartStep.PROCESS_ORDER:
        setPageTitle(intl.formatMessage({ id: 'cart.process_purchase' }));
        setBackButton({
          text: intl.formatMessage({ id: 'general.back_to_cart' }),
          onClick: () => setCurrent(CartStep.CART),
        });
        break;
      case CartStep.RECEIVE_ORDER:
        setPageTitle(
          intl.formatMessage({ id: 'cart.your_order_received_title' })
        );
        setBackButton({
          text: intl.formatMessage({ id: 'general.continue_shopping' }),
          onClick: () =>
            navigate(
              isEmarket
                ? PublicRoute.PRODUCTS_ALL
                : PublicRoute.WHOLESALE_PRODUCTS_ALL
            ),
        });
        break;
    }
  }, [current, intl]);

  useEffect(() => {
    const status = searchParams.get('status');

    if (status) {
      if (status === 'pending') {
        return;
      }

      localStorage.removeItem(PAYMENT_IN_PROGRESS_KEY);
      const pending = takePendingPaymentCart(marketType);

      if (status === 'success') {
        dispatch({ type: CartActionType.CLEAR_CART });
        setCurrent(CartStep.RECEIVE_ORDER);

        const orderId = searchParams.get('order_id');
        if (orderId) {
          setReceivedOrderError(null);
          fetchReceivedOrder({}, { orderId });
        } else {
          setReceivedOrderError('error');
        }

        return;
      }

      if (pending) {
        restoreUnlessPaid(pending);
        return;
      }

      const failedOrderId = searchParams.get('order_id');
      if (failedOrderId) {
        pendingRestoreRef.current = null;
        checkStashedOrder({}, { orderId: failedOrderId });
      }

      return;
    }

    if (!readPaymentInProgress()) {
      const pending = takePendingPaymentCart(marketType);

      if (pending) {
        restoreUnlessPaid(pending);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    const step = searchParams.get('PROCESS_ORDER');

    if (tab) {
      const numericTab = parseInt(tab, 10);
      setCurrent(numericTab as CartStep);
    }

    if (step && isEmarket) {
      setCurrent(CartStep.PROCESS_ORDER);
    }
  }, [searchParams]);

  useEffect(() => {
    const pending = localStorage.getItem('pendingRepeatOrder');
    if (!pending) return;

    localStorage.removeItem('pendingRepeatOrder');

    const data = JSON.parse(pending) as {
      products: { product: ProductData; amount: number }[];
      wholesaleDeliveryMode: WholesaleDeliveryMode;
      wholesaleDeliveries: WholesaleDeliveryCondition[];
    };

    data.products.forEach(({ product, amount }) => {
      dispatch({
        type: CartActionType.ADD_ITEM,
        product,
        amount,
      });
    });

    if (data.wholesaleDeliveryMode && data.wholesaleDeliveries?.length) {
      dispatch({
        type: CartActionType.SET_WHOLESALE_DELIVERY_MODE,
        mode: data.wholesaleDeliveryMode,
      });
      dispatch({
        type: CartActionType.SET_WHOLESALE_DELIVERIES,
        deliveries: data.wholesaleDeliveries,
      });
    }

    setRepeatOrderProcessed(true);
  }, []);

  const baseSteps: StepProps[] = isEmarket
    ? [
        {
          style: { display: 'flex', justifyContent: 'flex-start' },
          title: intl.formatMessage({ id: 'general.basket' }),
        },
        {
          style: { display: 'flex', justifyContent: 'center' },
          title: intl.formatMessage({ id: 'general.process_your_purchase' }),
        },
        {
          style: { display: 'flex', justifyContent: 'flex-end' },
          title: intl.formatMessage({ id: 'general.order_receipt' }),
        },
      ]
    : [
        {
          style: { display: 'flex', justifyContent: 'flex-start' },
          title: intl.formatMessage({ id: 'general.basket' }),
        },
        {
          style: { display: 'flex', justifyContent: 'flex-end' },
          title: intl.formatMessage({ id: 'general.order_receipt' }),
        },
      ];

  const wholesaleStepMap: Record<number, number> = {
    [CartStep.CART]: 0,
    [CartStep.RECEIVE_ORDER]: 1,
  };

  const displayCurrent = isEmarket ? current : (wholesaleStepMap[current] ?? 0);

  const stepsItems = baseSteps.map((step, index) => {
    const item: StepProps = { ...step };

    if (index === displayCurrent) {
      item.status = 'process';
    } else {
      item.status = 'wait';
    }

    return item;
  });

  const steps = (
    <StyledAntdCartSteps
      type="navigation"
      current={displayCurrent}
      items={stepsItems}
    />
  );

  return (
    <DefaultLayout.PageLayout>
      <DefaultLayout.PageContent>
        <StyledCartPage>
          {contextHolder}
          <div className="back-button" onClick={backButton.onClick}>
            <Icon name={'arrow-left'} type={'any'} />
            <span>{backButton.text}</span>
          </div>
          <div className="container">
            {!pendingOrderNumber && (
              <div className={'cart-page-title'}>
                <h1>{pageTitle}</h1>
              </div>
            )}
            {!pendingOrderNumber && steps}
            {pendingOrderNumber && (
              <Alert
                type="warning"
                showIcon
                message={`${intl.formatMessage({
                  id: 'cart.payment_pending_message',
                })} — ${pendingOrderNumber}`}
                description={intl.formatMessage({
                  id: isPendingTimedOut
                    ? 'cart.payment_pending_timeout'
                    : 'cart.payment_pending_description',
                })}
                action={
                  isPendingTimedOut ? (
                    <div className="pending-actions">
                      <Button
                        label={intl.formatMessage({ id: 'general.my_orders' })}
                        onClick={() =>
                          navigate(
                            PublicRoute.PROFILE.replace(':tab', 'my_orders')
                          )
                        }
                      />
                      <Button
                        label={intl.formatMessage({
                          id: 'cart.payment_pending_continue',
                        })}
                        onClick={() => navigate(PublicRoute.PRODUCTS_ALL)}
                      />
                    </div>
                  ) : undefined
                }
              />
            )}
            {!pendingOrderNumber &&
              (isEmarket ? (
                <Divider />
              ) : (
                <>
                  {farmProducts?.length > 1 && (
                    <StyledNotification>
                      {intl.formatMessage({ id: 'cart_summary.notification' })}
                    </StyledNotification>
                  )}
                </>
              ))}
            {!pendingOrderNumber &&
              current === CartStep.CART &&
              (repeatOrderProcessed ? (
                <Cart
                  priceTotal={priceTotal}
                  farmProducts={farmProducts}
                  setCurrent={setCurrent}
                  current={current}
                  isView={false}
                  form={!isEmarket ? form : undefined}
                />
              ) : (
                <Spinner spinning={true}>
                  <div />
                </Spinner>
              ))}
            {current === CartStep.PROCESS_ORDER && isEmarket && (
              <ProcessPurchase
                priceTotal={priceTotal}
                setCurrent={setCurrent}
                farmProducts={farmProducts}
                current={current}
                isView={false}
                form={form}
                isBuying={isBuying}
                userForm={userForm}
                handleBuyProducts={handleBuyProducts}
              />
            )}
            {current === CartStep.RECEIVE_ORDER && receivedOrderError && (
              <Alert
                type={
                  receivedOrderError === 'forbidden' ? 'success' : 'warning'
                }
                showIcon
                message={intl.formatMessage({
                  id:
                    receivedOrderError === 'forbidden'
                      ? 'cart.receipt_login_message'
                      : 'cart.receipt_error_message',
                })}
                description={intl.formatMessage(
                  {
                    id:
                      receivedOrderError === 'forbidden'
                        ? 'cart.receipt_login_description'
                        : 'cart.receipt_error_description',
                  },
                  { number: searchParams.get('order_id') ?? '' }
                )}
                action={
                  <Button
                    label={intl.formatMessage({
                      id:
                        receivedOrderError === 'forbidden'
                          ? 'cart.receipt_login_button'
                          : 'cart.receipt_error_button',
                    })}
                    onClick={() =>
                      navigate(
                        receivedOrderError === 'forbidden'
                          ? PublicRoute.LOGIN
                          : PublicRoute.PROFILE.replace(':tab', 'my_orders')
                      )
                    }
                  />
                }
              />
            )}
            {current === CartStep.RECEIVE_ORDER &&
              !receivedOrderError &&
              (receivedOrder ? (
                <Cart
                  priceTotal={receivedOrder.priceTotal}
                  farmProducts={receivedOrder.farmProducts}
                  setCurrent={setCurrent}
                  current={current}
                  isView={true}
                />
              ) : (
                <Spinner spinning={true}>
                  <div />
                </Spinner>
              ))}
            {!pendingOrderNumber && !isEmarket && current === CartStep.CART && (
              <StyledWholesalePaymentBlock gap={5} justify="end">
                <div className={'cart-summary-final-price'}>
                  <div className={'cart-summary-final-price-title'}>
                    <span className="desktop-label">
                      {intl.formatMessage({ id: 'cart_summary.total' })}
                    </span>
                    <span className="mobile-label">
                      {intl.formatMessage({
                        id: 'cart_summary.amount_be_paid',
                      })}
                    </span>
                  </div>
                  <div className={'cart-summary-price'}>
                    {Math.max(priceTotal, 0).toFixed(2)} €
                  </div>
                </div>
                {current === CartStep.CART && <WholesaleDelivery />}
                <Tooltip
                  title={
                    !wholesaleDeliveryMode || !wholesaleDeliveries?.length
                      ? intl.formatMessage({
                          id: 'cart_summary.delivery_required',
                        })
                      : ''
                  }
                >
                  <span>
                    <Button
                      onClick={() => handleBuyProducts('PRE_PAID')}
                      className={'cart-summary-submit-btn'}
                      label={intl.formatMessage({
                        id: 'cart_summary.pre_paid',
                      })}
                      loading={isBuying}
                      size="large"
                      disabled={
                        !priceTotal ||
                        !wholesaleDeliveryMode ||
                        !wholesaleDeliveries?.length
                      }
                    />
                  </span>
                </Tooltip>
              </StyledWholesalePaymentBlock>
            )}
          </div>
        </StyledCartPage>
        <SmartIdModal
          open={isOpenSmartId}
          setIsOpen={setIsOpenSmartId}
          fingerPrint={fingerPrint}
        />
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};
